import { Component, ElementRef, ViewChild } from '@angular/core';
import { CONFIG } from '../../config';
import {
  MYGOORU_AI_CONTEXT_TYPE,
  INIT_ACTION_TYPE,
  USERCATEGORY,
  LOGINTYPE,
  NEW_USER,
} from '../../constants/helper-constant';
import { isTabView, generateUUID } from '../../utils';
import { UserService } from '@gooru/my-gooru-ts';
import { UserContextService } from '../../providers/services/user-context.service';
import { SessionService } from '@gooru/navigator-core-widget-ui';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '@gooru/navigator-auth-widget-ui';
import { AuthServiceHelper } from '../../providers/services/auth.service';
import { pairwise } from 'rxjs/operators';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
})
export class HomeComponent {
  @ViewChild('aiContainer') public aiContainer!: ElementRef;
  public mygContext: any;
  public isTabView!: boolean;
  public userContext: {
    contact: string;
    contactType: 'phone' | 'email' | 'external';
    countryDialingCode?: string;
    countryCode?: string;
    loginType?: string | null;
    first_name?: string | null;
    role?: string | null;
    user_id?: string | null;
  } | null = null;
  public cred!: string;
  public isLearner!: boolean;
  public isInstructor!: boolean;
 
  constructor(
    private userService: UserService,
    private userContextService: UserContextService,
    private sessionService: SessionService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private authService: AuthService,
    private authServiceHelper: AuthServiceHelper
  ) {
    this.cred = this.activatedRoute.snapshot.queryParams['cred'];
    // Use pairwise to detect when session changes from valid to null (actual logout)
    this.sessionService.observeSessionChanges().pipe(pairwise()).subscribe(([prevSession, currentSession]) => {
      if (prevSession && !currentSession) {
        // Force page refresh on logout to clear all in-memory state
        window.location.href = '/login';
      }
    });
  }

  // -------------------------------------------------------------------------
  // Lifecycle methods
  public ngOnInit() {
    this.isTabView = isTabView();
    this.initializeContext();
  }

  /**
   * @function initializeContext
   * validateForm method used to initialize the content for mygooru
   */
  private async initializeContext() {
    const userInfo = this.userService.getUserInfo();
    const userInfoContext = userInfo?.userContext;
    let myUser: any = {};
    this.userContext = this.userContextService.getContact();
    (window as any).IS_MYGOORU_AI = true;
    const { version, appId } = CONFIG;
    let { clientId, clientKey } = CONFIG;
    if (this.cred) {
      const [id, key] = atob(this.cred).split(':');
      clientId = id;
      clientKey = key;
    }
    if (
      userInfoContext &&
      (this.userContext?.loginType === LOGINTYPE.CREDENTIAL || this.userContext?.contactType === 'external')
    ) {
      const hostname = window.location.hostname;
      if (hostname.startsWith('learner')) {
        this.isLearner = true;
      } else if (hostname.startsWith('instructor')) {
        this.isInstructor = true;
      }
      let myUser = {
        role: userInfo.userContext.role || USERCATEGORY?.STUDENT,
        id: userInfoContext?.id,
        email: userInfoContext?.email,
        username: userInfoContext?.username ||
        userInfoContext?.firstname ||
        this.userContext?.contact ,
        access_token: userInfoContext?.access_token
      };
      await this.userService.setUserSession(
        clientId,
        clientKey,
        appId,
        myUser,
        userInfoContext?.access_token,
        userInfo.refreshToken
      );
      this.mygContext = {
        version,
        clientId,
        clientKey,
        appId,
        context: {
          type: MYGOORU_AI_CONTEXT_TYPE,
          action: INIT_ACTION_TYPE,
          user: myUser,
        },
      };
    } else {
      myUser = {
        role: this.userContext?.role || USERCATEGORY.STUDENT,
        firstname: this.userContext?.first_name || 'Guest',
        access_code: null
      };
      if (this.userContext) {
        const contact = this.userContext.contact || '';
        const safeContact =
          this.userContext.contactType === 'phone' && !contact.startsWith('anonymous_')
            ? `anonymous_${contact}`
            : contact;
        myUser.id = this.userContext?.user_id || contact;
        if (this.userContext.contactType === 'email') {
          myUser.email = contact;
          myUser.username = contact;
        } else {
          if(safeContact?.includes('explore')) {
            myUser.username = 'John Doe';
          } else if (!(safeContact.includes('@gooru.org') && safeContact.includes('anonymous'))) {
            myUser.email = `${safeContact}@gooru.org`;
            myUser.username = safeContact;
          }
        }      
      } else {
        // Fallback to generate a UUID if no context
        myUser.id = generateUUID();
      }
      if(this.userContext?.contactType === 'email') {
        const userDetails = await this.authService?.signInAnonymous(clientId, clientKey);
        const anon_access_token = userDetails?.access_token;
        const userData = await this.authServiceHelper?.getUserData(this.userContext?.contact, anon_access_token);
        if(userData?.reference_id) {
          myUser.id = userData?.reference_id;
          myUser.first_name = userData?.first_name;
          myUser.last_name = userData?.last_name;
          myUser.email = userData?.email;
          myUser.username = userData?.username;
        }
      }
      this.mygContext = {
        version,
        clientId,
        clientKey,
        appId,
        context: {
          type: MYGOORU_AI_CONTEXT_TYPE,
          action: INIT_ACTION_TYPE,
          user: myUser,
        },
      };
    }
  }

  /**
   * @function getCookie
   * this method to get cookies
   */
  getCookie(name: string): string | null {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? decodeURIComponent(match[2]) : null;
  }

  /**
   * @function widgetResizer
   * validateForm method used to resize the container
   */
  public widgetResizer() {
    if (!this.isTabView) {
      setTimeout(() => {
        const element = document.querySelector(
          '#myg-widget-container .myg-widget-panel'
        );
        if (element) {
          const resizeObserver: any = new ResizeObserver((entries) => {
            for (let entry of entries) {
              const width = entry?.contentRect?.width + 50;
              if (this.aiContainer && this.aiContainer.nativeElement) {
                this.aiContainer.nativeElement.style.gridTemplateColumns = `1fr ${width}px`;
              }
            }
          });
          resizeObserver.observe(element);
        }
      }, 1000);
    }
  }
}
